import { memo, ReactNode, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import { InlineRenameField } from '@rossum/components/inline-rename/InlineRenameField';
import { useInlineRename } from '@rossum/components/inline-rename/useInlineRename';
import {
  IconChevronDown,
  IconDotsVertical,
  IconEdit,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import {
  alpha,
  Button,
  Collapse,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { useRenameWorkspace } from '../../../business/workspaces/useRenameWorkspace';
import { QueueTilesSkeleton } from './queue-tile/Skeletons';

const WORKSPACE_ACTIONS_CLASS = 'workspace-actions';

export type WorkspaceSectionAction = 'delete' | 'addQueue';

type WorkspaceSectionProps = {
  id: number;
  name: string;
  queues: Queue[];
  queuesAreLoading: boolean;
  renderQueues: (queues: Queue[]) => ReactNode;
  onAction: (action: WorkspaceSectionAction) => void;
};

export const WorkspaceSection = memo(
  ({
    id,
    name,
    queues,
    queuesAreLoading,
    renderQueues,
    onAction,
  }: WorkspaceSectionProps) => {
    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement>(null);

    const hasQueues = queues.length > 0;

    const [isExpanded, setIsExpanded] = useState(true);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const { mutate: renameWorkspace } = useRenameWorkspace();

    const handleCloseMenu = () => setAnchorEl(null);

    const {
      isRenaming,
      editedName,
      setEditedName,
      handleStartRename,
      handleSaveRename,
      handleCancelRename,
      handleKeyDown,
    } = useInlineRename({
      initialName: name,
      inputRef,
      onSave: newName => renameWorkspace({ workspaceId: id, newName }),
      onStartRename: handleCloseMenu,
    });

    const handleToggle = () => setIsExpanded(prev => !prev);

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleRemove = () => {
      onAction('delete');
      handleCloseMenu();
    };

    const isEmpty = !hasQueues && !queuesAreLoading;

    return (
      <Stack
        spacing={2}
        sx={{
          [`&:is(:hover, :focus) .${WORKSPACE_ACTIONS_CLASS}`]: {
            visibility: 'visible',
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            flex={1}
            minWidth={0}
          >
            {!isEmpty ? (
              <IconButton
                onClick={handleToggle}
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: t =>
                    t.transitions.create('transform', {
                      duration: t.transitions.duration.short,
                    }),
                  '&:hover': {
                    color: theme => theme.palette.action.disabled,
                    textDecoration: 'none',
                  },
                }}
              >
                <SvgIcon fontSize="medium">
                  <IconChevronDown />
                </SvgIcon>
              </IconButton>
            ) : null}
            <InlineRenameField
              name={name}
              isRenaming={isRenaming}
              editedName={editedName}
              setEditedName={setEditedName}
              handleKeyDown={handleKeyDown}
              handleSaveRename={handleSaveRename}
              handleCancelRename={handleCancelRename}
              inputRef={inputRef}
              variant="h5"
            />
          </Stack>
          <IconButton
            className={WORKSPACE_ACTIONS_CLASS}
            size="small"
            onClick={handleOpenMenu}
            sx={{
              visibility: anchorEl || isRenaming ? 'visible' : 'hidden',
            }}
          >
            <SvgIcon
              fontSize="medium"
              sx={{
                color: t => alpha(t.palette.action.active, 0.56),
              }}
            >
              <IconDotsVertical />
            </SvgIcon>
          </IconButton>
        </Stack>

        <Stack spacing={2}>
          {hasQueues ? (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Stack spacing={0.5}>{renderQueues(queues)}</Stack>
            </Collapse>
          ) : queuesAreLoading ? (
            <QueueTilesSkeleton />
          ) : (
            <Typography variant="body2" color="text.secondary" px={2}>
              {intl.formatMessage({
                id: 'features.queueManager.emptyWorkspace',
              })}
            </Typography>
          )}

          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => onAction('addQueue')}
            >
              {intl.formatMessage({
                id: 'features.queueManager.actions.addQueue',
              })}
            </Button>
          </Stack>
        </Stack>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          autoFocus
          slotProps={{
            list: {
              dense: true,
            },
          }}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transitionDuration={{
            exit: 0,
            enter: 250,
          }}
        >
          <ListItem disablePadding>
            <ListItemButton onClick={handleStartRename}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <SvgIcon fontSize="medium">
                  <IconEdit />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText>
                {intl.formatMessage({
                  id: 'components.workspaces.menu.labels.rename',
                })}
              </ListItemText>
            </ListItemButton>
          </ListItem>
          <Tooltip
            title={
              hasQueues
                ? intl.formatMessage({
                    id: 'components.workspaces.menu.errors.deleteForbidden',
                  })
                : undefined
            }
            placement="right"
            enterDelay={600}
            slotProps={{
              tooltip: {
                sx: { textAlign: 'left' },
              },
            }}
          >
            <ListItem disablePadding>
              <ListItemButton
                disabled={hasQueues}
                onClick={hasQueues ? undefined : handleRemove}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <SvgIcon fontSize="medium">
                    <IconTrash />
                  </SvgIcon>
                </ListItemIcon>
                <ListItemText>
                  {intl.formatMessage({
                    id: 'components.workspaces.menu.labels.delete',
                  })}
                </ListItemText>
              </ListItemButton>
            </ListItem>
          </Tooltip>
        </Menu>
      </Stack>
    );
  }
);
